<template>
    <div class="sb position-relative" :class="{ 'is-open': isOpen, 'is-disabled': disabled }" ref="root" @keydown.stop.prevent="onKeydown" tabindex="0">
        <!-- Control -->
        <div class="d-flex align-items-center justify-content-between bg-white" :class="[
            controlClass,                 // form-control + size
            { 'is-valid': isValid, 'is-invalid': isInvalid },  // BS validation classes
            disabled ? 'disabled' : ''
        ]" role="combobox" :aria-expanded="isOpen ? 'true' : 'false'" :aria-disabled="disabled ? 'true' : 'false'" :aria-controls="dropdownId" @click="toggleOpen">
            <span v-if="selectedOption" class="sb__value text-truncate">
                <!-- Custom selected render -->
                <slot name="selected" :option="selectedOption">
                    {{ getLabel(selectedOption) }}
                </slot>
            </span>

            <span v-else class="sb__placeholder text-muted">
                {{ placeholder }}
            </span>

            <span class="d-inline-flex align-items-center gap-2">
                <button v-if="clearable && !disabled && hasValue" class="btn btn-sm btn-link p-0 text-secondary" type="button" @click.stop="clear" aria-label="Clear" title="Clear">
                    ×
                </button>
                <span class="text-secondary">
                    <span class="sb__arrow"><svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M15.9188 6H9.68877H4.07877C3.11877 6 2.63877 7.21426 3.31877 7.92607L8.49877 13.3484C9.32877 14.2172 10.6788 14.2172 11.5088 13.3484L13.4788 11.2862L16.6888 7.92607C17.3588 7.21426 16.8788 6 15.9188 6Z"
                                fill="#AAABB7" />
                        </svg>
                    </span>
                </span>
            </span>
        </div>

        <!-- Feedback (Bootstrap) -->
        <div v-if="isValid && validFeedback" class="valid-feedback d-block">
            {{ validFeedback }}
        </div>
        <div v-if="isInvalid && invalidFeedback" class="invalid-feedback d-block">
            {{ invalidFeedback }}
        </div>
        <!-- Or slots -->
        <slot v-if="isValid && !validFeedback" name="valid-feedback"></slot>
        <slot v-if="isInvalid && !invalidFeedback" name="invalid-feedback"></slot>

        <!-- Dropdown -->
        <div v-if="isOpen" :id="dropdownId" class="dropdown-menu show w-100 mt-1 p-0" style="max-height: 280px; overflow: hidden;" role="listbox">
            <!-- Search -->
            <div v-if="searchable" class="p-2 border-bottom">
                <input ref="searchInput" v-model="query" type="text" class="form-control" :placeholder="searchPlaceholder" @keydown.stop />
            </div>

            <!-- List -->
            <div class="list-group list-group-flush" style="max-height: 220px; overflow: auto;">
                <button v-for="(opt, idx) in filteredOptions" :key="getKey(opt, idx)" type="button" class="dropdown-item d-flex justify-content-between align-items-center" :class="{
                    active: idx === activeIndex,
                    disabled: isOptionDisabled(opt)
                }" @mouseenter="setActive(idx)" @click="select(opt)" role="option" :aria-selected="isSelected(opt) ? 'true' : 'false'">
                    <span class="text-truncate">
                        <slot name="option" :option="opt">
                            {{ getLabel(opt) }}
                        </slot>
                    </span>
                    <span v-if="isSelected(opt)" class="ms-2 text-primary">✓</span>
                </button>

                <div v-if="filteredOptions.length === 0" class="dropdown-item text-muted">
                    {{ emptyText }}
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "SelectBox",
    props: {
        // v-model
        value: { type: [String, Number, Boolean, Object, null], default: null },

        // data
        options: { type: Array, default: () => [] }, // [{ value, label, disabled? }] or ["a","b"]
        placeholder: { type: String, default: "" },
        disabled: { type: Boolean, default: false },
        clearable: { type: Boolean, default: false },

        // search
        searchable: { type: Boolean, default: true },
        searchPlaceholder: { type: String, default: "Search..." },
        emptyText: { type: String, default: "No data available." },

        // mapping keys
        valueKey: { type: String, default: "value" },
        labelKey: { type: String, default: "label" },
        disabledKey: { type: String, default: "disabled" },

        // model format
        valueFormat: {
            type: String,
            default: "id",
            validator: (v) => ["id", "object"].includes(v),
        },

        // ✅ Bootstrap-first
        size: {
            type: String,
            default: "md", // 'sm' | 'md' | 'lg'
            validator: (v) => ["sm", "md", "lg"].includes(v),
        },
        isValid: { type: Boolean, default: false },
        isInvalid: { type: Boolean, default: false },
        validFeedback: { type: String, default: "" },
        invalidFeedback: { type: String, default: "" },
        idPrefix: { type: String, default: "sb" }, // để tạo id dropdown
    },

    data() {
        return {
            isOpen: false,
            query: "",
            activeIndex: 0,
            localId: Math.random().toString(36).slice(2, 9),
        };
    },

    computed: {
        dropdownId() {
            return `${this.idPrefix}-dropdown-${this.localId}`;
        },
        controlClass() {
            // form-control + size map
            switch (this.size) {
                case "sm": return "form-control form-control-sm";
                case "lg": return "form-control form-control-lg";
                default: return "form-control";
            }
        },

        normalizedOptions() {
            return (this.options || []).map((o) => {
                if (o && typeof o === "object") return o;
                return { [this.valueKey]: o, [this.labelKey]: String(o) };
            });
        },

        currentId() {
            if (this.valueFormat === "object") {
                return this.value && typeof this.value === "object"
                    ? this.value[this.valueKey]
                    : null;
            }
            return this.value;
        },

        selectedOption() {
            return (
                this.normalizedOptions.find((o) => this.getValue(o) === this.currentId) || null
            );
        },

        filteredOptions() {
            const q = (this.query || "").trim().toLowerCase();
            if (!this.searchable || !q) return this.normalizedOptions;
            return this.normalizedOptions.filter((o) =>
                this.getLabel(o).toLowerCase().includes(q)
            );
        },

        hasValue() {
            if (this.valueFormat === "object") {
                return !!(this.value && typeof this.value === "object");
            }
            return this.value != null && this.value !== "";
        },
    },

    watch: {
        isOpen(open) {
            if (open) {
                const idx = this.filteredOptions.findIndex((o) => this.isSelected(o));
                this.activeIndex = idx >= 0 ? idx : 0;

                this.$nextTick(() => {
                    if (this.searchable && this.$refs.searchInput) {
                        this.$refs.searchInput.focus();
                    }
                });
            } else {
                this.query = "";
            }
        },
        query() {
            this.activeIndex = 0;
        },
    },

    mounted() {
        document.addEventListener("click", this.handleClickOutside, true);
    },
    beforeDestroy() {
        document.removeEventListener("click", this.handleClickOutside, true);
    },

    methods: {
        getValue(opt) {
            return opt ? opt[this.valueKey] : null;
        },
        getLabel(opt) {
            return opt ? String(opt[this.labelKey]) : "";
        },
        isOptionDisabled(opt) {
            return !!(opt && opt[this.disabledKey]);
        },
        getKey(opt, idx) {
            const v = this.getValue(opt);
            return v != null ? v : idx;
        },

        isSelected(opt) {
            return this.getValue(opt) === this.currentId;
        },

        toModelValue(opt) {
            if (this.valueFormat === "object") return opt;
            return this.getValue(opt);
        },

        toggleOpen() {
            if (this.disabled) return;
            this.isOpen = !this.isOpen;
            if (this.isOpen) this.$refs.root && this.$refs.root.focus();
        },
        close() {
            this.isOpen = false;
        },

        clear() {
            this.$emit("input", null);
            this.$emit("change", null);
            this.close();
        },

        select(opt) {
            if (this.disabled || this.isOptionDisabled(opt)) return;

            const payload = this.toModelValue(opt);
            this.$emit("input", payload);   // for v-model
            this.$emit("change", payload);  // optional
            this.close();
        },

        setActive(idx) {
            this.activeIndex = idx;
        },

        onKeydown(e) {
            if (this.disabled) return;

            const max = this.filteredOptions.length - 1;

            if (!this.isOpen) {
                if (["Enter", " ", "ArrowDown"].includes(e.key)) {
                    this.isOpen = true;
                }
                return;
            }

            if (e.key === "Escape") {
                this.close();
                return;
            }

            if (e.key === "ArrowDown") {
                this.activeIndex = Math.min(this.activeIndex + 1, max);
                this.scrollActiveIntoView();
                return;
            }

            if (e.key === "ArrowUp") {
                this.activeIndex = Math.max(this.activeIndex - 1, 0);
                this.scrollActiveIntoView();
                return;
            }

            if (e.key === "Enter") {
                const opt = this.filteredOptions[this.activeIndex];
                if (opt) this.select(opt);
                return;
            }
        },

        scrollActiveIntoView() {
            this.$nextTick(() => {
                const list = this.$el.querySelector(".list-group");
                const items = this.$el.querySelectorAll(".dropdown-item");
                const active = items[this.activeIndex];
                if (!list || !active) return;

                const listRect = list.getBoundingClientRect();
                const itemRect = active.getBoundingClientRect();

                if (itemRect.top < listRect.top) {
                    list.scrollTop -= (listRect.top - itemRect.top);
                } else if (itemRect.bottom > listRect.bottom) {
                    list.scrollTop += (itemRect.bottom - listRect.bottom);
                }
            });
        },

        handleClickOutside(e) {
            if (!this.isOpen) return;
            const root = this.$refs.root;
            if (root && !root.contains(e.target)) {
                this.close();
            }
        },
    },
};
</script>

<style scoped>
.sb :deep(.form-control.disabled),
.sb :deep(.form-control[disabled]) {
    cursor: not-allowed;
    opacity: .7;
}

.sb__value,
.sb__placeholder {
    flex: 1 1 auto;
    min-width: 0;
}
</style>